import React, { useEffect, useRef } from "react";
import * as styles from "./FriendlyCaptcha.module.scss";
import { WidgetInstance, WidgetInstanceOptions } from "friendly-challenge";
import { LanguageCode } from "src/utils/localization/languages/types";
import { WebQuoteError } from "components/WebQuoteModal/utils";
import { isPresent } from "src/utils/checks";

type CaptchaLanguage = "en" | "es" | "de" | "nl" | "fr" | "it" | "pt";

interface FriendlyCaptchaProps {
  doneCallback: WidgetInstanceOptions["doneCallback"];
  errorCallback: WidgetInstanceOptions["doneCallback"];
  languageCode: LanguageCode;
  validationError: WebQuoteError | undefined;
}

export const FriendlyCaptcha: React.FC<
  React.PropsWithChildren<FriendlyCaptchaProps>
> = ({ doneCallback, errorCallback, languageCode, validationError }) => {
  const container = useRef<HTMLDivElement>(null);
  const widget = useRef<WidgetInstance>();

  const captchaLang = localeToCaptchaLang(languageCode);

  useEffect(() => {
    if (!container.current) return;
    let realContainer = container.current.querySelector("div");

    if (!realContainer) {
      realContainer = document.createElement("div");
      realContainer.classList.add("frc-captcha");
      realContainer.classList.add(styles.container);
      realContainer.setAttribute("data-sitekey", "FCMV2F0P8HQEJLEJ");
      realContainer.setAttribute("data-lang", captchaLang);
      container.current.replaceChildren(realContainer);
    }

    widget.current = new WidgetInstance(realContainer, {
      startMode: "auto",
      doneCallback,
      errorCallback
    });

    return () => {
      if (widget.current != undefined) {
        widget.current.destroy();
        widget.current = undefined;
      }
    };
  }, [captchaLang, doneCallback, errorCallback]);

  useEffect(() => {
    if (isPresent(validationError)) {
      widget.current?.reset();
    }
  }, [validationError]);

  return <div ref={container}></div>;
};

const localeToCaptchaLang = (locale: LanguageCode): CaptchaLanguage => {
  switch (locale) {
    case "pt-br":
      return "pt";

    default:
      return locale;
  }
};

export const defaultCaptchaErrorHandler: FriendlyCaptchaProps["errorCallback"] =
  (error) => {
    console.error("FriendlyCaptcha error:", error);
  };
